import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode
  label?: string
  badge?: number
  variant?: 'default' | 'ghost'
  size?: 'sm' | 'md'
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, badge, variant = 'default', size = 'md', className, ...props }, ref) => {
    const sizeClass = size === 'sm' ? 'h-9 w-9' : 'h-10 w-10'
    const iconSize = size === 'sm' ? 'h-[18px] w-[18px]' : 'h-5 w-5'

    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        className={cn(
          'group relative inline-flex items-center justify-center rounded-full border transition-all duration-200 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wd-orange active:scale-95 disabled:opacity-50',
          variant === 'default' &&
            'border-ink/10 bg-paper text-carbon hover:border-ink/30 hover:bg-oyster',
          variant === 'ghost' && 'border-transparent bg-transparent text-carbon hover:bg-oyster',
          sizeClass,
          className
        )}
        {...props}
      >
        <span className={cn('transition-transform duration-200 ease-out-expo group-active:scale-90', iconSize)}>
          {icon}
        </span>
        {typeof badge === 'number' && badge > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-wd-orange px-1 text-xs font-semibold leading-none text-white">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
    )
  }
)

IconButton.displayName = 'IconButton'
